/**
 * Notification Configuration Registry
 *
 * Single source of truth for all notification types, their preference schemas,
 * and default values. Imported by both the Convex backend and React frontend.
 */

// ── Preference Field Configs ────────────────────────────────────────────

/**
 * A boolean preference field (e.g., "enabled", "emailEnabled").
 */
export type BooleanFieldConfig = {
  type: "boolean";
  label: string;
  default: boolean;
};

/**
 * A number-array preference field with validation constraints
 * (e.g., timing days before expiration).
 */
export type NumberArrayFieldConfig = {
  type: "numberArray";
  label: string;
  default: number[];
  validation: {
    /** Minimum allowed value for each element */
    min: number;
    /** Maximum number of elements in the array */
    maxValues: number;
    /** Maximum allowed value for each element */
    maxValue: number;
  };
};

/**
 * Union of all supported preference field configurations.
 */
export type PreferenceFieldConfig = BooleanFieldConfig | NumberArrayFieldConfig;

// ── Notification Type Config ────────────────────────────────────────────

/**
 * Configuration for a single notification type.
 * Each notification type defines its category, metadata for the UI,
 * and a preferences schema that drives both validation and auto-generated settings.
 */
export type NotificationTypeConfig = {
  /** Grouping category for the settings UI */
  category: string;
  /** Human-readable name shown in settings */
  label: string;
  /** Short description shown below the label */
  description: string;
  /** Icon identifier (lucide icon name) for the settings UI */
  icon: string;
  /** Map of preference field keys to their configuration */
  preferences: Record<string, PreferenceFieldConfig>;
};

// ── Notification Types Registry ─────────────────────────────────────────

/**
 * The registry of all notification types in the system.
 * Add new notification types here; the backend validator, preference helpers,
 * and settings UI will automatically pick them up.
 */
export const NOTIFICATION_TYPES = {
  documentExpiration: {
    category: "documents",
    label: "Document Expiration",
    description: "Get notified when documents are about to expire.",
    icon: "FileWarning",
    preferences: {
      enabled: {
        type: "boolean",
        label: "Enable notifications",
        default: true,
      },
      emailEnabled: {
        type: "boolean",
        label: "Send email notifications",
        default: true,
      },
      timing: {
        type: "numberArray",
        label: "Notify me",
        default: [14, 7, 0],
        validation: {
          min: 0,
          maxValues: 5,
          maxValue: 365,
        },
      },
    },
  },
  auditStatusChange: {
    category: "audits",
    label: "Audit Status Changes",
    description:
      "Get notified when an audit is submitted, approved, or rejected.",
    icon: "ClipboardCheck",
    preferences: {
      enabled: {
        type: "boolean",
        label: "Enable notifications",
        default: true,
      },
      emailEnabled: {
        type: "boolean",
        label: "Send email notifications",
        default: true,
      },
      notifyOnSubmission: {
        type: "boolean",
        label: "Notify on submission",
        default: true,
      },
      notifyOnApproval: {
        type: "boolean",
        label: "Notify on approval",
        default: true,
      },
      notifyOnRejection: {
        type: "boolean",
        label: "Notify on rejection",
        default: true,
      },
    },
  },
  newFinding: {
    category: "findings",
    label: "New Finding",
    description: "Get notified when a new finding is confirmed for an audit.",
    icon: "Search",
    preferences: {
      enabled: {
        type: "boolean",
        label: "Enable notifications",
        default: true,
      },
      emailEnabled: {
        type: "boolean",
        label: "Send email notifications",
        default: true,
      },
    },
  },
  findingResolved: {
    category: "findings",
    label: "Finding Resolved",
    description: "Get notified when a finding is marked as resolved.",
    icon: "CheckCircle",
    preferences: {
      enabled: {
        type: "boolean",
        label: "Enable notifications",
        default: true,
      },
      emailEnabled: {
        type: "boolean",
        label: "Send email notifications",
        default: true,
      },
    },
  },
  auditDeadline: {
    category: "audits",
    label: "Audit Deadline",
    description: "Get notified when an audit deadline is approaching.",
    icon: "CalendarAlert",
    preferences: {
      enabled: {
        type: "boolean",
        label: "Enable notifications",
        default: true,
      },
      emailEnabled: {
        type: "boolean",
        label: "Send email notifications",
        default: true,
      },
      timing: {
        type: "numberArray",
        label: "Notify me",
        default: [90, 60, 30, 14, 7, 0],
        validation: {
          min: 0,
          maxValues: 10,
          maxValue: 365,
        },
      },
    },
  },
  checklistCompleted: {
    category: "audits",
    label: "Checklist Completed",
    description:
      "Get notified when all checklist items for an audit are completed.",
    icon: "ListChecks",
    preferences: {
      enabled: {
        type: "boolean",
        label: "Enable notifications",
        default: true,
      },
      emailEnabled: {
        type: "boolean",
        label: "Send email notifications",
        default: true,
      },
    },
  },
} as const satisfies Record<string, NotificationTypeConfig>;

// ── Derived Types ───────────────────────────────────────────────────────

/**
 * Union of all notification type keys (e.g., "documentExpiration").
 */
export type NotificationTypeKey = keyof typeof NOTIFICATION_TYPES;

/**
 * Inferred preference value type for a given field config.
 */
type InferPreferenceValue<T extends PreferenceFieldConfig> =
  T extends BooleanFieldConfig
    ? boolean
    : T extends NumberArrayFieldConfig
      ? number[]
      : never;

/**
 * Inferred preferences shape for a single notification type config.
 */
type InferNotificationPreferences<T extends NotificationTypeConfig> = {
  [K in keyof T["preferences"]]: InferPreferenceValue<T["preferences"][K]>;
};

/**
 * Full default preferences object shape, keyed by notification type.
 */
export type DefaultPreferences = {
  [K in NotificationTypeKey]: InferNotificationPreferences<
    (typeof NOTIFICATION_TYPES)[K]
  >;
};

// ── Helper Functions ────────────────────────────────────────────────────

/**
 * Returns the default preferences for all notification types.
 * Each field's default value comes from its config definition above,
 * ensuring a single source of truth.
 */
export function getDefaultPreferences(): DefaultPreferences {
  const result: Record<string, Record<string, unknown>> = {};

  for (const [typeKey, typeConfig] of Object.entries(NOTIFICATION_TYPES)) {
    const prefs: Record<string, unknown> = {};
    for (const [fieldKey, fieldConfig] of Object.entries(
      typeConfig.preferences,
    )) {
      prefs[fieldKey] = fieldConfig.default;
    }
    result[typeKey] = prefs;
  }

  return result as DefaultPreferences;
}
